import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Banner } from "@/types/media";

export default function HeroCarousel({ banners }: { banners: Banner[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || banners.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, banners.length]);

  const goTo = (next: number) => {
    setIndex((next + banners.length) % banners.length);
  };

  const active = banners[index];
  if (!active) return null;

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden md:h-[600px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {banners.map((banner, i) => (
        <div
          key={banner.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={banner.image}
            alt={banner.title}
            title={`${banner.title} - 91影视`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] items-center px-4 md:px-8">
        <div key={active.id} className="max-w-xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-fill"></i>
            {active.tag}
          </span>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h1>
          <p className="mt-3 max-w-lg text-sm text-background-200 md:text-base">{active.subtitle}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.movieId}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-6 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-secondary-400"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              查看详情
            </Link>
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-2.5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              <i className="ri-compass-3-line text-lg"></i>
              浏览全部
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {banners.map((banner, i) => (
          <button
            key={banner.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-7 bg-secondary-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}